import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import DesktopLayout from './components/layout/DesktopLayout';
import Login from './components/auth/Login';
import LoginMobileOtp from './components/auth/LoginMobileOtp';
import ProtectedRoute from './components/auth/ProtectedRoute';
import FinanceDashboard from './pages/FinanceDashboard';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailsPage from './pages/ProjectDetailsPage';
import ProjectExpenseCartexPrintPage from './pages/ProjectExpenseCartexPrintPage';
import ProjectLedgerVoucherPrintPage from './pages/ProjectLedgerVoucherPrintPage';
import ExpenseTypesPage from './pages/ExpenseTypesPage';
import BankAccountsPage from './pages/BankAccountsPage';
import BankAccountDetailsPage from './pages/BankAccountDetailsPage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeProfilePage from './pages/EmployeeProfilePage';
import AccountingDocumentEntryPage from './pages/AccountingDocumentEntryPage';
import AccountingDocumentsPage from './pages/AccountingDocumentsPage';
import AccountingDocumentDetailsPage from './pages/AccountingDocumentDetailsPage';
import AccountingDocumentPrintPage from './pages/AccountingDocumentPrintPage';
import AccountingSettingsPage from './pages/AccountingSettingsPage';
import AccountingSettingsHomePage from './pages/AccountingSettingsHomePage';
import PayrollCalculatorPage from './pages/PayrollCalculatorPage';
import ProfilePage from './pages/ProfilePage';
import AccountingAccountReportPage from './pages/AccountingAccountReportPage';
import AccountingBankMovementsPage from './pages/AccountingBankMovementsPage';
import ChequeBooksPage from './pages/ChequeBooksPage';
import ChequeRegisterPage from './pages/ChequeRegisterPage';
import ChequePrintPage from './pages/ChequePrintPage';
import ChequeDisplaySettingsPage from './pages/ChequeDisplaySettingsPage';
import BsiInternetBankPage from './pages/banks/bsi';
import BanksListPage from './pages/BanksListPage';
import { bsiServicePath, parseBsiTabSlug } from './pages/banks/bsi/helpers';

function BsiLegacyInternetBankRedirect() {
  const { section } = useParams<{ section?: string }>();
  const tab = parseBsiTabSlug(section) ?? 'home';
  return <Navigate to={bsiServicePath(tab)} replace />;
}

const App: React.FC = () => {
  React.useEffect(() => {
    try {
      localStorage.removeItem('accounting_counterparty_ledger_v1');
      localStorage.removeItem('accounting_counterparty_ledger_print_v1');
      localStorage.removeItem('accounting_mock_expense_entries_v1');
      localStorage.removeItem('accounting_mock_expense_types_v1');
    } catch {
      // ignore
    }
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login-otp" element={<LoginMobileOtp />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/projects/:id/expense-cartex/print" element={<ProjectExpenseCartexPrintPage />} />
        <Route path="/projects/:id/ledger-voucher/print" element={<ProjectLedgerVoucherPrintPage />} />
        <Route path="/accounting-documents/:id/print" element={<AccountingDocumentPrintPage />} />
        <Route path="/cheque-books/print/:id" element={<ChequePrintPage />} />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route element={<DesktopLayout />}>
          <Route path="/" element={<FinanceDashboard />} />
          <Route path="/expenses" element={<Navigate to="/accounting-documents" replace />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<Navigate to="overview" relative replace />} />
          <Route path="/projects/:id/beneficiary/:beneficiaryKey" element={<ProjectDetailsPage />} />
          <Route path="/projects/:id/:section" element={<ProjectDetailsPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/employees/:id" element={<EmployeeProfilePage />} />
          <Route path="/payroll-calculator" element={<PayrollCalculatorPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/financial-accounting" element={<Navigate to="/" replace />} />
          <Route path="/financial-accounting/*" element={<Navigate to="/" replace />} />
          <Route path="/accounting-reports/account-turnover" element={<AccountingAccountReportPage />} />
          <Route path="/accounting-bank-movements" element={<AccountingBankMovementsPage />} />
          <Route path="/counterparties" element={<Navigate to="/accounting-documents" replace />} />
          <Route path="/counterparties/*" element={<Navigate to="/accounting-documents" replace />} />
          <Route path="/accounting-documents" element={<AccountingDocumentsPage />} />
          <Route path="/accounting-documents/new" element={<AccountingDocumentEntryPage />} />
          <Route path="/accounting-documents/:id/edit" element={<AccountingDocumentEntryPage />} />
          <Route path="/accounting-documents/:id" element={<AccountingDocumentDetailsPage />} />
          <Route path="/accounting-settings" element={<AccountingSettingsHomePage />} />
          <Route path="/accounting-settings/moodian" element={<Navigate to="/accounting-settings" replace />} />
          <Route path="/accounting-settings/cheque" element={<ChequeDisplaySettingsPage />} />
          <Route path="/accounting-settings/print" element={<AccountingSettingsPage />} />
          <Route path="/tax-invoices" element={<Navigate to="/" replace />} />
          <Route path="/tax-invoices/*" element={<Navigate to="/" replace />} />
          <Route path="/expense-types" element={<ExpenseTypesPage />} />
          <Route path="/bank-accounts" element={<BankAccountsPage />} />
          <Route path="/bank-accounts/:id" element={<BankAccountDetailsPage />} />
          <Route path="/banks" element={<BanksListPage />} />
          <Route path="/banks/bsi" element={<BsiInternetBankPage />} />
          <Route path="/banks/bsi/:section" element={<BsiInternetBankPage />} />
          <Route path="/bsi-internet-bank" element={<Navigate to="/banks/bsi" replace />} />
          <Route path="/bsi-internet-bank/:section" element={<BsiLegacyInternetBankRedirect />} />
          <Route path="/cheque-books/register" element={<ChequeRegisterPage />} />
          <Route path="/cheque-books" element={<ChequeBooksPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default App;
